import {
  collection,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  query,
  where,
  orderBy,
  getDocs,
  onSnapshot,
  serverTimestamp,
  writeBatch,
  Unsubscribe
} from 'firebase/firestore'
import { User } from 'firebase/auth'
import { db } from '@/lib/firebase'
import {
  Earning,
  CreateEarningData,
  UpdateEarningData,
  EarningValidationError
} from '@/types/earnings'
import { PeriodService } from './periodService'

// Validation utilities
export class EarningsValidator {
  static validateEarning(data: CreateEarningData | UpdateEarningData): EarningValidationError[] {
    const errors: EarningValidationError[] = []

    // Validate driverId if provided (required for create)
    if ('driverId' in data) {
      if (!data.driverId || data.driverId.trim().length === 0) {
        errors.push({ field: 'driverId', message: 'Förare är obligatorisk' })
      }
    }

    // Validate period if provided (required for create)
    if ('period' in data) {
      if (!data.period || data.period.trim().length === 0) {
        errors.push({ field: 'period', message: 'Period är obligatorisk' })
      }
      // Validate period format
      else if (!/^\d{4}-\d{2}-01$/.test(data.period)) {
        errors.push({ field: 'period', message: 'Period måste vara i format yyyy-mm-01' })
      }
    }

    // Validate uploadSessionId if provided (required for create)
    if ('uploadSessionId' in data) {
      if (!data.uploadSessionId || data.uploadSessionId.trim().length === 0) {
        errors.push({ field: 'uploadSessionId', message: 'Uppladdningssession är obligatorisk' })
      }
    }

    // Validate platform if provided (required for create)
    if ('platform' in data) {
      if (!data.platform || data.platform.trim().length === 0) {
        errors.push({ field: 'platform', message: 'Plattform är obligatorisk' })
      }
    }

    // Validate grossEarnings if provided
    if ('grossEarnings' in data && data.grossEarnings !== undefined) {
      if (typeof data.grossEarnings !== 'number' || data.grossEarnings < 0) {
        errors.push({ field: 'grossEarnings', message: 'Bruttoinkomst måste vara ett positivt tal' })
      }
    }

    // Validate netEarnings if provided
    if ('netEarnings' in data && data.netEarnings !== undefined) {
      if (typeof data.netEarnings !== 'number' || data.netEarnings < 0) {
        errors.push({ field: 'netEarnings', message: 'Nettoinkomst måste vara ett positivt tal' })
      }
    }

    // Validate tips if provided
    if ('tips' in data && data.tips !== undefined) {
      if (typeof data.tips !== 'number' || data.tips < 0) {
        errors.push({ field: 'tips', message: 'Dricks måste vara ett positivt tal' })
      }
    }


    return errors
  }
}


// Main Earnings Service
export class EarningsService {
  private static readonly COLLECTION_NAME = 'earnings'

  // Get collection reference for a user
  private static getUserCollection(user: User) {
    return collection(db, 'users', user.uid, this.COLLECTION_NAME)
  }

  // Get document reference for a specific earning
  private static getEarningDocument(user: User, earningId: string) {
    return doc(db, 'users', user.uid, this.COLLECTION_NAME, earningId)
  }

  // Convert Firestore document to Earning
  private static convertFirestoreDoc(doc: any): Earning {
    const data = doc.data()
    return {
      id: doc.id,
      driverId: data.driverId,
      period: data.period,
      uploadSessionId: data.uploadSessionId,
      platform: data.platform,
      grossEarnings: data.grossEarnings || 0,
      netEarnings: data.netEarnings || 0,
      tips: data.tips || 0,
      cashReceived: data.cashReceived || 0,
      isDeleted: data.isDeleted || false,
      createdAt: data.createdAt?.toDate() || new Date(),
      updatedAt: data.updatedAt?.toDate() || new Date(),
    }
  }

  // Create new earning
  static async createEarning(user: User, data: CreateEarningData): Promise<Earning> {
    try {
      // Validate input
      const errors = EarningsValidator.validateEarning(data)
      if (errors.length > 0) {
        throw new Error(errors[0].message)
      }

      // Check if period is locked
      const isLocked = await PeriodService.isPeriodLocked(user, data.period)
      if (isLocked) {
        throw new Error('Perioden är låst. Du måste låsa upp perioden innan du kan skapa nya intäkter.')
      }

      // Create new earning document with auto-generated ID
      const docRef = doc(this.getUserCollection(user))
      const now = serverTimestamp()
      
      const earningData = {
        driverId: data.driverId.trim(),
        period: data.period.trim(),
        uploadSessionId: data.uploadSessionId.trim(),
        platform: data.platform,
        grossEarnings: data.grossEarnings,
        netEarnings: data.netEarnings,
        tips: data.tips,
        cashReceived: data.cashReceived || 0,
        createdAt: now,
        updatedAt: now,
      }

      await setDoc(docRef, earningData)
      
      // Get the created earning
      const createdEarning = await this.getEarning(user, docRef.id)
      if (!createdEarning) {
        throw new Error('Det gick inte att skapa inkomstposten')
      }
      
      
      return createdEarning
    } catch (error) {
      console.error('Error creating earning:', error)
      if (error instanceof Error) {
        throw error
      }
      throw new Error('Det gick inte att skapa inkomstposten')
    }
  }

  // Update existing earning
  static async updateEarning(user: User, id: string, updates: UpdateEarningData): Promise<Earning> {
    try {
      // Validate input
      const errors = EarningsValidator.validateEarning(updates)
      if (errors.length > 0) {
        throw new Error(errors[0].message)
      }

      const docRef = this.getEarningDocument(user, id)

      // Check if earning exists
      const existingDoc = await getDoc(docRef)
      if (!existingDoc.exists()) {
        throw new Error('Inkomstposten hittades inte')
      }

      const existingEarning = existingDoc.data() as Earning

      // Check if period is locked
      const period = existingEarning.period
      const isLocked = await PeriodService.isPeriodLocked(user, period)
      if (isLocked) {
        throw new Error('Perioden är låst. Du måste låsa upp perioden innan du kan ändra intäkter.')
      }

      // Prepare update data
      const updateData: any = {
        updatedAt: serverTimestamp()
      }

      if (updates.grossEarnings !== undefined) {
        updateData.grossEarnings = updates.grossEarnings
      }
      if (updates.netEarnings !== undefined) {
        updateData.netEarnings = updates.netEarnings
      }
      if (updates.tips !== undefined) {
        updateData.tips = updates.tips
      }
      if (updates.cashReceived !== undefined) {
        updateData.cashReceived = updates.cashReceived
      }

      await updateDoc(docRef, updateData)
      
      // Get updated earning
      const updatedEarning = await this.getEarning(user, id)
      if (!updatedEarning) {
        throw new Error('Det gick inte att uppdatera inkomstposten')
      }
      
      
      return updatedEarning
    } catch (error) {
      console.error('Error updating earning:', error)
      if (error instanceof Error) {
        throw error
      }
      throw new Error('Det gick inte att uppdatera inkomstposten')
    }
  }

  // Delete earning
  static async deleteEarning(user: User, id: string): Promise<void> {
    try {
      const docRef = this.getEarningDocument(user, id)

      // Check if earning exists
      const existingDoc = await getDoc(docRef)
      if (!existingDoc.exists()) {
        throw new Error('Inkomstposten hittades inte')
      }

      const existingEarning = existingDoc.data() as Earning

      // Check if period is locked
      const period = existingEarning.period
      const isLocked = await PeriodService.isPeriodLocked(user, period)
      if (isLocked) {
        throw new Error('Perioden är låst. Du måste låsa upp perioden innan du kan radera intäkter.')
      }

      // For now, we'll do a hard delete. Could implement soft delete later if needed
      await updateDoc(docRef, {
        isDeleted: true,
        updatedAt: serverTimestamp()
      })


    } catch (error) {
      console.error('Error deleting earning:', error)
      if (error instanceof Error) {
        throw error
      }
      throw new Error('Det gick inte att ta bort inkomstposten')
    }
  }

  // Delete all earnings for a specific upload session ID (soft delete)
  static async deleteEarningsByUploadId(user: User, uploadSessionId: string): Promise<number> {
    try {
      console.log(`[EarningsService] Starting deletion for uploadSessionId: ${uploadSessionId}`)

      // Get all earnings for this upload session (without isDeleted filter to avoid index issues)
      const q = query(
        this.getUserCollection(user),
        where('uploadSessionId', '==', uploadSessionId)
      )

      const querySnapshot = await getDocs(q)
      console.log(`[EarningsService] Found ${querySnapshot.docs.length} earnings documents for uploadSessionId: ${uploadSessionId}`)

      if (querySnapshot.empty) {
        console.log(`[EarningsService] No earnings found for upload session: ${uploadSessionId}`)
        return 0 // No earnings to delete
      }

      // Filter out already deleted earnings and count them
      const nonDeletedDocs = querySnapshot.docs.filter(doc => {
        const data = doc.data()
        return !data.isDeleted
      })

      console.log(`[EarningsService] ${nonDeletedDocs.length} non-deleted earnings found to delete`)

      if (nonDeletedDocs.length === 0) {
        console.log(`[EarningsService] All earnings already deleted for upload session: ${uploadSessionId}`)
        return 0
      }

      // Use batch operation for efficient bulk updates
      const batch = writeBatch(db)
      const now = serverTimestamp()

      nonDeletedDocs.forEach(doc => {
        console.log(`[EarningsService] Marking earning ${doc.id} as deleted`)
        const docRef = this.getEarningDocument(user, doc.id)
        batch.update(docRef, {
          isDeleted: true,
          updatedAt: now
        })
      })

      // Commit the batch
      console.log(`[EarningsService] Committing batch deletion of ${nonDeletedDocs.length} earnings`)
      await batch.commit()

      const deletedCount = nonDeletedDocs.length
      console.log(`[EarningsService] Successfully soft deleted ${deletedCount} earnings for upload session: ${uploadSessionId}`)

      return deletedCount
    } catch (error) {
      console.error('Error deleting earnings by upload ID:', error)
      if (error instanceof Error) {
        throw error
      }
      throw new Error('Det gick inte att ta bort inkomstposterna för denna uppladdning')
    }
  }

  // Get single earning by ID
  static async getEarning(user: User, id: string): Promise<Earning | null> {
    try {
      const docRef = this.getEarningDocument(user, id)
      const docSnap = await getDoc(docRef)
      
      if (!docSnap.exists()) {
        return null
      }
      
      const earning = this.convertFirestoreDoc(docSnap)
      
      
      return earning
    } catch (error) {
      console.error('Error fetching earning:', error)
      throw new Error('Det gick inte att hämta inkomstposten')
    }
  }

  // Get earnings by driver ID
  static async getEarningsByDriver(user: User, driverId: string): Promise<Earning[]> {
    try {
      const q = query(
        this.getUserCollection(user),
        where('driverId', '==', driverId),
        orderBy('createdAt', 'desc')
      )

      const querySnapshot = await getDocs(q)
      const earnings = querySnapshot.docs
        .map(doc => this.convertFirestoreDoc(doc))
        .filter(earning => !earning.isDeleted)


      return earnings
    } catch (error) {
      console.error('Error fetching earnings by driver:', error)
      throw new Error('Det gick inte att hämta förarens inkomster')
    }
  }

  /**
   * Soft-delete all earnings for a driver in open (unlocked) periods only.
   * Earnings in locked periods are silently skipped.
   * Used when deleting a driver to clean up their data.
   *
   * @returns Object with count of deleted earnings and skipped (locked) earnings
   */
  static async deleteEarningsByDriverInOpenPeriods(
    user: User,
    driverId: string
  ): Promise<{ deleted: number; skippedLocked: number }> {
    try {
      const earnings = await this.getEarningsByDriver(user, driverId)

      if (earnings.length === 0) {
        return { deleted: 0, skippedLocked: 0 }
      }

      // Get unique periods and check which are locked
      const periods = [...new Set(earnings.map(e => e.period))]
      const periodLockStatus = await Promise.all(
        periods.map(async period => ({
          period,
          isLocked: await PeriodService.isPeriodLocked(user, period)
        }))
      )
      const lockedPeriods = new Set(
        periodLockStatus.filter(p => p.isLocked).map(p => p.period)
      )

      // Separate earnings into deletable and skipped
      const toDelete = earnings.filter(e => !lockedPeriods.has(e.period))
      const skipped = earnings.filter(e => lockedPeriods.has(e.period))

      // Batch delete earnings in open periods
      if (toDelete.length > 0) {
        const batch = writeBatch(db)
        toDelete.forEach(earning => {
          const docRef = this.getEarningDocument(user, earning.id)
          batch.update(docRef, {
            isDeleted: true,
            updatedAt: serverTimestamp()
          })
        })
        await batch.commit()
      }

      return { deleted: toDelete.length, skippedLocked: skipped.length }
    } catch (error) {
      console.error('Error deleting earnings by driver in open periods:', error)
      throw new Error('Det gick inte att radera förarens intäkter')
    }
  }

  // Get earnings by period
  static async getEarningsByPeriod(user: User, period: string): Promise<Earning[]> {
    try {
      const q = query(
        this.getUserCollection(user),
        where('period', '==', period),
        orderBy('createdAt', 'desc')
      )
      
      const querySnapshot = await getDocs(q)
      const earnings = querySnapshot.docs
        .map(doc => this.convertFirestoreDoc(doc))
        .filter(earning => !earning.isDeleted)
      
      
      return earnings
    } catch (error) {
      console.error('Error fetching earnings by period:', error)
      throw new Error('Det gick inte att hämta periodens inkomster')
    }
  }

  // Get earnings by upload session ID
  static async getEarningsByUpload(user: User, uploadSessionId: string): Promise<Earning[]> {
    try {
      const q = query(
        this.getUserCollection(user),
        where('uploadSessionId', '==', uploadSessionId),
        orderBy('createdAt', 'desc')
      )
      
      const querySnapshot = await getDocs(q)
      const earnings = querySnapshot.docs
        .map(doc => this.convertFirestoreDoc(doc))
        .filter(earning => !earning.isDeleted)
      
      
      return earnings
    } catch (error) {
      console.error('Error fetching earnings by upload:', error)
      throw new Error('Det gick inte att hämta uppladdningens inkomster')
    }
  }

  // Subscribe to earnings changes (real-time updates)
  static subscribeToEarnings(user: User, callback: (earnings: Earning[]) => void): Unsubscribe {
    try {
      const q = query(
        this.getUserCollection(user),
        orderBy('createdAt', 'desc')
      )
      
      return onSnapshot(q, (querySnapshot) => {
        try {
          const earnings = querySnapshot.docs
            .map(doc => this.convertFirestoreDoc(doc))
            .filter(earning => !earning.isDeleted)
          callback(earnings)
        } catch (processingError) {
          console.error('Error processing earnings snapshot:', processingError)
          callback([])
        }
      }, (error) => {
        console.error('Error in earnings subscription:', error)
        // Check if it's an index error and provide helpful message
        if (error.message.includes('index')) {
          console.error('This appears to be an index error. Please create the required Firestore index.')
        }
        callback([])
      })
    } catch (error) {
      console.error('Error setting up earnings subscription:', error)
      return () => {} // Return empty unsubscribe function
    }
  }
}